import { S3Client } from '@aws-sdk/client-s3';
import { Upload } from '@aws-sdk/lib-storage';
import desktopApi from './desktopApi';
import type { OssUploadProgress } from './upload-service/oss.interface';

const APP_CENTER_ADMIN_BASE = 'v1/admin/app-center';
const ZIP_CONTENT_TYPE = 'application/zip';
const DEFAULT_PART_SIZE_BYTES = 8 * 1024 * 1024;
const DEFAULT_MAX_CONCURRENCY = 4;
const MIN_MULTIPART_PART_SIZE_BYTES = 5 * 1024 * 1024;

interface CreateAppArtifactUploadSessionRequest {
  appId: string;
  versionId: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  sha256: string;
}

interface AppArtifactSignedUploadSignature {
  uploadUrl?: string;
  upload_url?: string;
  headers?: Record<string, string>;
  contentType?: string;
  content_type?: string;
  sizeBytes?: number | string;
  size_bytes?: number | string;
  sha256?: string;
}

interface AppArtifactUploadCredentials {
  accessKeyId?: string;
  access_key_id?: string;
  secretAccessKey?: string;
  secret_access_key?: string;
  sessionToken?: string;
  session_token?: string;
  expiresAt?: string;
  expires_at?: string;
}

interface AppArtifactUploadConstraints {
  maxSizeBytes?: number | string;
  max_size_bytes?: number | string;
  contentType?: string;
  content_type?: string;
  sha256?: string;
  metadata?: Record<string, string>;
}

interface AppArtifactMultipartUpload {
  partSizeBytes?: number | string;
  part_size_bytes?: number | string;
  maxConcurrency?: number | string;
  max_concurrency?: number | string;
}

interface AppArtifactUploadSessionReply
  extends AppArtifactSignedUploadSignature {
  uploadSignature?: AppArtifactSignedUploadSignature;
  upload_signature?: AppArtifactSignedUploadSignature;
  artifactUrl?: string;
  artifact_url?: string;
  artifactURL?: string;
  objectKey?: string;
  object_key?: string;
  bucket?: string;
  region?: string;
  endpoint?: string;
  forcePathStyle?: boolean;
  force_path_style?: boolean;
  credentials?: AppArtifactUploadCredentials;
  uploadConstraints?: AppArtifactUploadConstraints;
  upload_constraints?: AppArtifactUploadConstraints;
  multipart?: AppArtifactMultipartUpload;
}

interface NormalizedSignedUploadSignature {
  uploadUrl: string;
  headers: Record<string, string>;
  contentType?: string;
  sizeBytes?: number | string;
  sha256?: string;
}

interface NormalizedAppArtifactUploadSession {
  uploadSignature: NormalizedSignedUploadSignature;
  artifactUrl: string;
  objectKey: string;
  bucket: string;
  region: string;
  endpoint: string;
  forcePathStyle: boolean;
  credentials: {
    accessKeyId: string;
    secretAccessKey: string;
    sessionToken: string;
  };
  uploadConstraints: {
    maxSizeBytes?: number | string;
    contentType?: string;
    sha256?: string;
    metadata: Record<string, string>;
  };
  multipart: {
    partSizeBytes?: number | string;
    maxConcurrency?: number | string;
  };
}

export interface AppArtifactUploadMetadata {
  artifactKind: 'zip';
  artifactUrl: string;
  artifactSha256: string;
  artifactSizeBytes: number;
  contentType: string;
  fileName: string;
  objectKey: string;
}

interface UploadAppArtifactZipOptions {
  appId: string;
  versionId: string;
  file: File;
  sha256?: string;
  onProgress?: (progress: OssUploadProgress) => void;
}

function normalizeUploadSession(
  reply: AppArtifactUploadSessionReply,
): NormalizedAppArtifactUploadSession {
  const signature = reply.uploadSignature ?? reply.upload_signature ?? reply;
  const credentials = reply.credentials ?? {};
  const uploadConstraints =
    reply.uploadConstraints ?? reply.upload_constraints ?? {};
  const multipart = reply.multipart ?? {};

  return {
    uploadSignature: {
      uploadUrl: signature.uploadUrl ?? signature.upload_url ?? '',
      headers: signature.headers ?? {},
      contentType: signature.contentType ?? signature.content_type,
      sizeBytes: signature.sizeBytes ?? signature.size_bytes,
      sha256: signature.sha256,
    },
    artifactUrl:
      reply.artifactUrl ?? reply.artifactURL ?? reply.artifact_url ?? '',
    objectKey: reply.objectKey ?? reply.object_key ?? '',
    bucket: reply.bucket ?? '',
    region: reply.region ?? '',
    endpoint: reply.endpoint ?? '',
    forcePathStyle: reply.forcePathStyle ?? reply.force_path_style ?? false,
    credentials: {
      accessKeyId: credentials.accessKeyId ?? credentials.access_key_id ?? '',
      secretAccessKey:
        credentials.secretAccessKey ?? credentials.secret_access_key ?? '',
      sessionToken: credentials.sessionToken ?? credentials.session_token ?? '',
    },
    uploadConstraints: {
      maxSizeBytes:
        uploadConstraints.maxSizeBytes ?? uploadConstraints.max_size_bytes,
      contentType:
        uploadConstraints.contentType ?? uploadConstraints.content_type,
      sha256: uploadConstraints.sha256,
      metadata: uploadConstraints.metadata ?? {},
    },
    multipart: {
      partSizeBytes: multipart.partSizeBytes ?? multipart.part_size_bytes,
      maxConcurrency: multipart.maxConcurrency ?? multipart.max_concurrency,
    },
  };
}

function bytesToHex(buffer: ArrayBuffer) {
  return Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

function numberValue(value: number | string | undefined) {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function assertZipFile(file: File) {
  if (!file.name.trim().toLowerCase().endsWith('.zip')) {
    throw new Error('请选择 .zip 文件');
  }
}

function getZipContentType(file: File) {
  return file.type.trim() || ZIP_CONTENT_TYPE;
}

function assertUploadConstraints(
  session: NormalizedAppArtifactUploadSession,
  file: File,
  contentType: string,
  sha256: string,
) {
  const maxSizeBytes = numberValue(session.uploadConstraints.maxSizeBytes);
  if (maxSizeBytes > 0 && file.size > maxSizeBytes) {
    throw new Error(`文件大小 ${file.size} 超过上传限制 ${maxSizeBytes}`);
  }

  const signedSize = numberValue(session.uploadSignature.sizeBytes);
  if (signedSize > 0 && file.size !== signedSize) {
    throw new Error(
      `文件大小 ${file.size} 与上传签名大小 ${signedSize} 不一致`,
    );
  }

  const signedContentType =
    session.uploadConstraints.contentType ??
    session.uploadSignature.contentType;
  if (signedContentType && signedContentType !== contentType) {
    throw new Error(
      `文件类型 ${contentType} 与上传签名类型 ${signedContentType} 不一致`,
    );
  }

  const signedSha256 =
    session.uploadConstraints.sha256 ?? session.uploadSignature.sha256;
  if (signedSha256 && signedSha256.toLowerCase() !== sha256) {
    throw new Error('文件 SHA256 与上传签名不一致');
  }
}

export async function calculateAppArtifactFileSha256(file: File) {
  const buffer = await file.arrayBuffer();
  const digest = await window.crypto.subtle.digest('SHA-256', buffer);
  return bytesToHex(digest);
}

async function createUploadSession(
  data: CreateAppArtifactUploadSessionRequest,
) {
  const response = (await desktopApi.post<AppArtifactUploadSessionReply>(
    `${APP_CENTER_ADMIN_BASE}/artifact-upload-sessions`,
    data,
  )) as unknown as AppArtifactUploadSessionReply;

  return normalizeUploadSession(response);
}

function uploadedArtifactUrl(artifactUrl: string) {
  return artifactUrl.trim();
}

function hasHeader(headers: Record<string, string>, name: string) {
  return Object.keys(headers).some(
    (key) => key.trim().toLowerCase() === name.toLowerCase(),
  );
}

async function putFileToSignedUrl(
  file: File,
  session: NormalizedAppArtifactUploadSession,
  contentType: string,
  onProgress?: (progress: OssUploadProgress) => void,
) {
  const signature = session.uploadSignature;
  if (!signature.uploadUrl.trim()) {
    throw new Error('上传签名缺少 uploadUrl');
  }

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', signature.uploadUrl, true);

    const headers = signature.headers ?? {};
    for (const [key, value] of Object.entries(headers)) {
      const headerName = key.trim();
      if (headerName && value) {
        xhr.setRequestHeader(headerName, value);
      }
    }
    if (!hasHeader(headers, 'content-type')) {
      xhr.setRequestHeader('Content-Type', contentType);
    }

    xhr.upload.onprogress = (event) => {
      const total = event.lengthComputable ? event.total : file.size;
      const loaded = event.loaded;
      onProgress?.({
        loaded,
        total,
        percent: total > 0 ? Math.min(100, (loaded / total) * 100) : 0,
      });
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.({
          loaded: file.size,
          total: file.size,
          percent: 100,
        });
        resolve();
        return;
      }

      reject(new Error(`上传失败，HTTP ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('上传失败，请检查网络'));
    xhr.onabort = () => reject(new Error('上传已取消'));
    xhr.send(file);
  });
}

function hasMultipartSession(session: NormalizedAppArtifactUploadSession) {
  return Boolean(
    session.bucket &&
      session.region &&
      session.objectKey &&
      session.credentials.accessKeyId &&
      session.credentials.secretAccessKey &&
      session.credentials.sessionToken,
  );
}

function normalizePartSize(value: number | string | undefined) {
  const partSize = numberValue(value) || DEFAULT_PART_SIZE_BYTES;
  return Math.max(partSize, MIN_MULTIPART_PART_SIZE_BYTES);
}

function normalizeMaxConcurrency(value: number | string | undefined) {
  const concurrency = Math.floor(numberValue(value) || DEFAULT_MAX_CONCURRENCY);
  return Math.max(1, concurrency);
}

async function uploadMultipartToS3(
  file: File,
  session: NormalizedAppArtifactUploadSession,
  contentType: string,
  onProgress?: (progress: OssUploadProgress) => void,
) {
  const client = new S3Client({
    region: session.region,
    endpoint: session.endpoint || undefined,
    forcePathStyle: session.forcePathStyle,
    requestChecksumCalculation: 'WHEN_REQUIRED',
    credentials: {
      accessKeyId: session.credentials.accessKeyId,
      secretAccessKey: session.credentials.secretAccessKey,
      sessionToken: session.credentials.sessionToken,
    },
  });
  const upload = new Upload({
    client,
    params: {
      Bucket: session.bucket,
      Key: session.objectKey,
      Body: file,
      ContentType: contentType,
      Metadata: session.uploadConstraints.metadata,
    },
    queueSize: normalizeMaxConcurrency(session.multipart.maxConcurrency),
    partSize: normalizePartSize(session.multipart.partSizeBytes),
    leavePartsOnError: false,
  });

  upload.on('httpUploadProgress', (progress) => {
    const total = progress.total ?? file.size;
    const loaded = progress.loaded ?? 0;
    onProgress?.({
      loaded,
      total,
      percent: total > 0 ? Math.min(100, (loaded / total) * 100) : 0,
    });
  });

  await upload.done();
  onProgress?.({
    loaded: file.size,
    total: file.size,
    percent: 100,
  });
}

export const appArtifactUploadApi = {
  async uploadZipArtifact({
    appId,
    versionId,
    file,
    sha256,
    onProgress,
  }: UploadAppArtifactZipOptions): Promise<AppArtifactUploadMetadata> {
    assertZipFile(file);

    const artifactSha256 =
      sha256 ?? (await calculateAppArtifactFileSha256(file));
    const contentType = getZipContentType(file);
    const session = await createUploadSession({
      appId,
      versionId,
      fileName: file.name,
      contentType,
      sizeBytes: file.size,
      sha256: artifactSha256,
    });

    assertUploadConstraints(session, file, contentType, artifactSha256);

    if (hasMultipartSession(session)) {
      await uploadMultipartToS3(file, session, contentType, onProgress);
    } else {
      await putFileToSignedUrl(file, session, contentType, onProgress);
    }

    const artifactUrl = uploadedArtifactUrl(session.artifactUrl);
    if (!artifactUrl) {
      throw new Error('上传成功但未返回 artifact URL');
    }

    return {
      artifactKind: 'zip',
      artifactUrl,
      artifactSha256,
      artifactSizeBytes: file.size,
      contentType,
      fileName: file.name,
      objectKey: session.objectKey,
    };
  },
};
